/* CRM Interactions: global store, toast system, modals, filters, search.
   Wraps the data arrays from crm-data.jsx with React state so mutations re-render.
   Exposes a <AppProvider> and useApp() hook. */

const { createContext, useContext, useReducer, useMemo, useCallback, useRef, useEffect: useEff, useState: useS } = React;

// ---------- Store ----------
const AppCtx = createContext(null);
const useApp = () => useContext(AppCtx);

const PROVINCES = [
  'Buenos Aires','CABA','Catamarca','Chaco','Chubut','Córdoba','Corrientes','Entre Ríos','Formosa',
  'Jujuy','La Pampa','La Rioja','Mendoza','Misiones','Neuquén','Río Negro','Salta','San Juan','San Luis',
  'Santa Cruz','Santa Fe','Santiago del Estero','Tierra del Fuego','Tucumán'
];
const ZONES = ['AMBA Norte','AMBA Sur','Interior Oeste','Interior Norte','Interior Este','Interior Sur','Cuyo','Patagonia'];
const ACTIVITIES = ['Constructora','Industrias','Contratista','Distribuidor Materiales','Casa de Electricidad/Ferretería','Fibra Óptica','Solar','Tableros eléctricos','Obras eléctricas','Distribución eléctrica','Cooperativa eléctrica'];
const ORIGINS = ['Mail','WhatsApp','Portal de licitación','Teléfono'];
// Antes solo se traducían Mail y WhatsApp: "Teléfono" y "Portal de licitación"
// caían los dos en MANUAL y se perdía por dónde había entrado el pedido.
// Fecha de hoy para un <input type="date">. Ojo: toISOString() devuelve UTC, asi
// que despues de las 21hs argentinas ya daba el dia siguiente.
function hoyInput() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const SOURCE_MAP = { 'Mail': 'EMAIL', 'WhatsApp': 'WHATSAPP', 'Teléfono': 'PHONE', 'Portal de licitación': 'WEB' };
const REJECT_REASONS = ['Precio','Plazo de entrega','Condición de pago','Competencia','Sin respuesta','Otro'];

function AppProvider({ children }) {
  const [quotes, setQuotes]   = useS(() => [...QUOTES]);
  const [clients, setClients] = useS(() => [...CLIENTS]);
  // allUsers = todos (incluye desactivados) — solo para RESOLVER nombres.
  // users    = solo activos — es lo que consumen los desplegables donde se asigna.
  const [allUsers, setUsers]  = useS(() => [...USERS]);
  const [activity, setActivity] = useS(() => [...ACTIVITY]);
  const [comments, setComments] = useS(() => ({...COMMENTS}));
  const [notifications, setNotifications] = useS([]);
  const [inboxAlerts, setInboxAlerts]     = useS([]);

  // ── Lectura de actividades (feed histórico) ──────────────────────────────────
  const NOTIF_STORAGE_KEY = 'crm_notif_read_ids';
  const getReadIds = () => {
    try { return new Set(JSON.parse(localStorage.getItem(NOTIF_STORAGE_KEY) || '[]')); }
    catch { return new Set(); }
  };
  const saveReadId = (id) => {
    const ids = getReadIds(); ids.add(id);
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify([...ids].slice(-500)));
  };
  const saveAllReadIds = (ids) => {
    const existing = getReadIds();
    ids.forEach(id => existing.add(id));
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify([...existing].slice(-500)));
  };

  // ── Snooze / dismiss de inbox alerts ────────────────────────────────────────
  // dismissKey: key del servidor ('overdue_stages' | 'idle_quotes')
  // days: 3 | 7 | 30
  const snoozeAlert = async (id, dismissKey, days) => {
    // Quitar de la vista inmediatamente (UX optimista)
    setInboxAlerts(prev => prev.filter(a => a.id !== id));
    if (!dismissKey) return; // alerta no-dismissable
    try {
      const token = localStorage.getItem('crm_token');
      await fetch('/api/notifications/dismiss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ key: dismissKey, days }),
      });
    } catch (e) {
      console.warn('dismiss error (no-crítico):', e.message);
    }
  };
  // mark-seen: actualiza lastInboxCheck en el servidor
  const markInboxSeen = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      await fetch('/api/notifications/mark-seen', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Limpiar newCount local para que los badges se actualicen
      setInboxAlerts(prev => prev.map(a => ({ ...a, newCount: 0 })));
    } catch (_) {}
  };
  const isSnoozed = (_id) => false; // ya no usamos localStorage para esto

  // ── Carga inicial de actividades ──────────────────────────────────────────────
  const mapActivity = (activities) => {
    const readIds = getReadIds();
    return activities.map(a => {
      let kind = 'info';
      if (a.action === 'STAGE_CHANGE') {
        kind = a.detail?.includes('rechazada') ? 'bad' : a.detail?.includes('aceptada') ? 'ok' : 'info';
      } else if (a.action === 'CREATED') kind = 'ok';
      const ref = a.quoteCode ? { kind: 'quote', code: a.quoteCode } : null;
      return { id: a.id, kind, text: a.detail, at: a.createdAt, read: readIds.has(a.id), ref, userName: a.userName };
    });
  };
  useEff(() => {
    CrmApi.getActivity(50).then(a => setNotifications(mapActivity(a))).catch(() => setNotifications([]));
  }, []);

  // Dispara un sync de mail al abrir el CRM (login fresco o pestaña con sesión
  // recordada) — no bloquea nada, corre en segundo plano. El backend tiene su
  // propio cooldown para no relanzarlo si ya corrió hace poco.
  useEff(() => {
    if (CrmAuth.isLoggedIn()) {
      CrmApi.syncMail().catch(() => {});
    }
  }, []);

  // ── Carga y polling de inbox alerts (cada 3 min) ─────────────────────────────
  const loadInboxAlerts = useRef(null);
  loadInboxAlerts.current = () => {
    CrmApi.getNotificationsInbox().then(alerts => {
      setInboxAlerts((alerts || []).filter(a => !isSnoozed(a.id)));
    }).catch(() => {});
  };
  // Solo consulta mientras la pestaña está a la vista (evita mantener despierto
  // el compute de Neon sin nadie mirando). Al volver: refresco inmediato.
  useEff(() => {
    let t = null;
    const stop  = () => { if (t) { clearInterval(t); t = null; } };
    const start = () => { stop(); t = setInterval(() => loadInboxAlerts.current(), 3 * 60 * 1000); };
    if (!document.hidden) { loadInboxAlerts.current(); start(); }
    const onVis = () => {
      if (document.hidden) stop();
      else { loadInboxAlerts.current(); start(); }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // ── Confirmar cotización asignada vista ("Listo ✓") ───────────────────────────
  const ackAssigned = useCallback(async (quoteId) => {
    setInboxAlerts(prev => prev.map(a => {
      if (a.type !== 'ASSIGNED_QUOTES') return a;
      const newItems = a.items.filter(i => i.id !== quoteId);
      return newItems.length > 0 ? { ...a, items: newItems, count: newItems.length } : null;
    }).filter(Boolean));
    try { await CrmApi.ackAssignedQuote(quoteId); } catch (e) { console.warn('ack error:', e.message); }
  }, []);

  // ── Confirmar mención en nota vista ("Ver") ───────────────────────────────────
  const ackMention = useCallback(async (noteId) => {
    setInboxAlerts(prev => prev.map(a => {
      if (a.type !== 'MENTIONS') return a;
      const newItems = a.items.filter(i => i.noteId !== noteId);
      return newItems.length > 0 ? { ...a, items: newItems, count: newItems.length } : null;
    }).filter(Boolean));
    try { await CrmApi.ackMention(noteId); } catch (e) { console.warn('ack error:', e.message); }
  }, []);

  // ── Smart sync: polling cada 25s, solo mientras la pestaña está a la vista ───
  // Con la pestaña oculta se corta del todo (evita mantener despierto el compute
  // de Neon sin que nadie esté mirando) — al volver, sync inmediato + se retoma.
  const lastSyncTs  = useRef(new Date().toISOString());
  const syncCounts  = useRef({ quotes: 0, clients: 0, users: 0 });
  const POLL_ACTIVE = 25 * 1000;   // 25s con pestaña activa

  // Helpers para mapear clients y users al formato del frontend
  const mapClientsArr = (arr) => arr.map(c => ({
    id: c.id, code: c.code, name: c.name, cuit: c.cuit || '',
    city: c.city || '', prov: c.province || '', zone: c.zone || '',
    activity: c.activity || '', seller: c.defaultSellerId || '',
    sellerName: c.defaultSeller?.name || '', legacySellerName: c.legacySellerName || '',
    email: c.emailPrimary || c.email || '',
    phone: c.phone || '', address: c.address || '',
  }));
  const roleMap = { DEVELOPER: 'Desarrollador', ADMIN: 'Administrador', VENDEDOR: 'Vendedor', LOGISTICA: 'Logística' };
  const mapUsersArr = (arr) => arr.map(u => ({
    id: u.id, name: u.name, email: u.email, role: roleMap[u.role] || u.role, zone: u.zone || '—',
    active: u.active !== false,
  }));

  const doSync = useRef(null);
  doSync.current = async () => {
    try {
      const data = await CrmApi.sync(lastSyncTs.current);
      lastSyncTs.current = data.ts;

      // 1. Merge updated quotes (upsert by code, preserva los existentes)
      if (data.quotes.length > 0) {
        setQuotes(prev => {
          const map = new Map(prev.map(q => [q.code, q]));
          data.quotes.forEach(q => map.set(q.code, q));
          return [...map.values()];
        });
      }

      // 2. Drift detection: si el count bajó, algo se eliminó → refresh completo
      const prev = syncCounts.current;
      if (prev.quotes > 0 && data.counts.quotes < prev.quotes) {
        const since = new Date(Date.now() - 365 * 86400 * 1000).toISOString().split('T')[0];
        CrmApi.getQuotes({ since }).then(fresh => setQuotes(fresh)).catch(() => {});
      }

      // 3. Clients: si cambió el count → refetch completo
      if (prev.clients > 0 && data.counts.clients !== prev.clients) {
        CrmApi.getClients().then(fresh => setClients(mapClientsArr(fresh))).catch(() => {});
      }

      // 4. Users: si cambió el count → refetch completo
      if (prev.users > 0 && data.counts.users !== prev.users) {
        CrmApi.getUsers().then(fresh => setUsers(mapUsersArr(fresh))).catch(() => {});
      }

      // 5. Activity: actualizar el feed de notificaciones
      if (data.activity.length > 0) {
        setNotifications(mapActivity(data.activity));
      }

      // Guardar counts para la próxima comparación (sin el de órdenes: esa fase ya no existe)
      syncCounts.current = { quotes: data.counts.quotes, clients: data.counts.clients, users: data.counts.users };
    } catch (_) { /* silencioso — no interrumpir al usuario */ }
  };

  useEff(() => {
    // Inicializar counts desde el estado actual
    syncCounts.current = {
      quotes: QUOTES.length,
      clients: CLIENTS.length, users: USERS.length,
    };

    let timer = null;
    const stop = () => { if (timer) { clearTimeout(timer); timer = null; } };
    const scheduleActive = () => {
      stop();
      timer = setTimeout(() => { doSync.current(); scheduleActive(); }, POLL_ACTIVE);
    };
    if (!document.hidden) scheduleActive();

    // Al ocultar la pestaña: cortar el polling. Al volver: sync inmediato +
    // retomar el polling activo. (La campanita maneja su propia visibilidad.)
    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        doSync.current();
        scheduleActive();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibilityChange); };
  }, []);

  // Quote-level filters (shared by board)
  const [quoteFilters, setQuoteFilters] = useS({ seller:'', client:'', period:'30d', zone:'', activity:'', min:'', max:'', sort:'recent', priority:'', ids:null, idsLabel:'' });

  // Logged-in user (for notes)
  const [currentUserId, setCurrentUserId] = useS('');
  // role view
  const [roleKey, setRoleKey] = useS('admin');

  // ---- Toasts ----
  const [toasts, setToasts] = useS([]);
  const toastIdRef = useRef(0);
  const pushToast = useCallback((text, tone='ok') => {
    const id = ++toastIdRef.current;
    setToasts(ts => [...ts, { id, text, tone }]);
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 3200);
  }, []);

  // ---- ID generators ----
  const nextQuoteCode = useCallback((qs) => {
    const nums = qs.map(q => parseInt(q.code.split('-').pop(),10)).filter(n => !isNaN(n));
    const next = (Math.max(0, ...nums) + 1).toString().padStart(3, '0');
    return `COT-${new Date().getFullYear()}-${next}`;
  }, []);
  const nextClientCode = useCallback((cs) => {
    const nums = cs.map(c => parseInt(c.code.split('-').pop(),10)).filter(n => !isNaN(n));
    const next = (Math.max(0, ...nums) + 1).toString().padStart(3, '0');
    return `CLI-${next}`;
  }, []);

  // ---- Mutators ----
  const addQuote = useCallback((partial) => {
    setQuotes(qs => {
      const code = nextQuoteCode(qs);
      const q = {
        code,
        client: partial.client,
        seller: partial.seller,
        stage: 'recibida',
        ingreso: partial.ingreso || hoyInput(),
        dias: 0,
        monto: partial.monto || null,
        adj: partial.file ? 1 : 0,
        notas: 0,
        flexxus: '',
        origin: partial.origin,
        observaciones: partial.observaciones || '',
        fechaLimite: partial.fechaLimite,
      };
      pushToast(`Cotización ${code} creada correctamente`);
      return [q, ...qs];
    });
  }, [nextQuoteCode, pushToast]);

  const addClient = useCallback((partial) => {
    setClients(cs => {
      const code = nextClientCode(cs);
      const c = { code, ...partial };
      pushToast(`Cliente ${partial.name} registrado`);
      return [c, ...cs];
    });
  }, [nextClientCode, pushToast]);

  const updateQuote = useCallback((code, patch) => {
    setQuotes(qs => qs.map(q => q.code === code ? { ...q, ...patch } : q));
  }, []);

  const moveQuoteStage = useCallback(async (code, stageId) => {
    const stg = STAGES_F1.find(s => s.id === stageId);
    const quote = quotes.find(q => q.code === code);
    if (!quote) return;
    const prevStage = quote.stage;
    // Optimistic update
    setQuotes(qs => qs.map(q => q.code === code ? { ...q, stage: stageId } : q));
    try {
      await CrmApi.changeQuoteStage(quote.id, stageId);
      pushToast(`Etapa actualizada a ${stg?.label}`);
      if (stageId === 'aceptada') {
        const freshQuotes = await CrmApi.getQuotes();
        if (freshQuotes) setQuotes(freshQuotes);
      }
    } catch (err) {
      setQuotes(qs => qs.map(q => q.code === code ? { ...q, stage: prevStage } : q));
      pushToast(err.message || 'Error al actualizar etapa', 'bad');
    }
  }, [quotes, pushToast]);

  const addComment = useCallback((code, text) => {
    setComments(cs => {
      const prev = cs[code] || [];
      return { ...cs, [code]: [...prev, { by: currentUserId, at: new Date().toISOString(), text }] };
    });
    // bump note count on quote
    setQuotes(qs => qs.map(q => q.code === code ? { ...q, notas: (q.notas||0)+1 } : q));
    pushToast('Nota agregada');
  }, [currentUserId, pushToast]);

  // inviteUser eliminado — InviteUserModal ahora llama directo a CrmApi.createUser

  const markNotificationRead = useCallback((id) => {
    setNotifications(ns => ns.map(n => n.id === id ? { ...n, read:true } : n));
    saveReadId(id);
  }, []);
  const markAllNotificationsRead = useCallback(() => {
    setNotifications(ns => {
      saveAllReadIds(ns.map(n => n.id));
      return ns.map(n => ({ ...n, read:true }));
    });
  }, []);

  // ---- Modals stack ----
  const [modals, setModals] = useS([]); // array of { kind, props }
  const openModal = useCallback((kind, props={}) => setModals(m => [...m, { kind, props }]), []);
  const closeModal = useCallback(() => setModals(m => m.slice(0, -1)), []);
  const closeAllModals = useCallback(() => setModals([]), []);

  const users = useMemo(() => allUsers.filter(u => u.active !== false), [allUsers]);

  const value = {
    quotes, setQuotes, clients, setClients, users, allUsers, setUsers, activity, comments, notifications,
    inboxAlerts, setInboxAlerts, snoozeAlert, markInboxSeen, ackAssigned, ackMention,
    quoteFilters, setQuoteFilters,
    currentUserId, setCurrentUserId, roleKey, setRoleKey,
    addQuote, addClient, updateQuote,
    moveQuoteStage, addComment,
    markNotificationRead, markAllNotificationsRead,
    pushToast, toasts,
    openModal, closeModal, closeAllModals, modals,
  };

  return (
    <AppCtx.Provider value={value}>
      {children}
      <ModalHost/>
      <ToastHost/>
      <MaintenanceOverlay/>
    </AppCtx.Provider>
  );
}

// ---------- Maintenance Overlay ----------
// Se activa cuando cualquier pedido a la API vuelve con { error: 'maintenance' }
// (ver apiFetch en crm-api.jsx). Solo DEVELOPER queda exento del bloqueo del
// lado del servidor, así que este cartel es lo único que ve todo el resto.
function MaintenanceOverlay() {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const onMaintenance = () => setShow(true);
    window.addEventListener('crm:maintenance', onMaintenance);
    return () => window.removeEventListener('crm:maintenance', onMaintenance);
  }, []);
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-navy-950/95 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-7 text-center">
        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <Icon name="wrench" size={22} className="text-amber-600"/>
        </div>
        <div className="font-semibold text-base text-ink-800 mb-1.5">Sistema en mantenimiento</div>
        <div className="text-[13px] text-ink-500 leading-relaxed mb-5">
          Estamos haciendo un ajuste puntual. Volvé a intentar en unos minutos.
        </div>
        <button
          onClick={() => window.location.reload()}
          className="w-full py-2.5 rounded-lg bg-navy-900 text-white text-[13px] font-medium hover:bg-navy-800 transition-colors">
          Reintentar
        </button>
      </div>
    </div>
  );
}

// ---------- Toast Host ----------
function ToastHost() {
  const { toasts } = useApp();
  const iconFor = t => t==='ok'?'check-circle':t==='bad'?'alert-circle':t==='warn'?'alert-triangle':'info';
  const toneFor = t => t==='ok'?'bg-emerald-600':t==='bad'?'bg-red-600':t==='warn'?'bg-orange-500':'bg-navy-900';
  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col gap-2 pointer-events-none">
      {toasts.map(t => (
        <div key={t.id} className={cx('pointer-events-auto modal-enter text-white px-4 py-3 rounded-xl shadow-pop flex items-center gap-2.5 min-w-[280px] max-w-sm', toneFor(t.tone))}>
          <Icon name={iconFor(t.tone)} size={16}/>
          <div className="text-[13px] font-medium leading-snug flex-1">{t.text}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- Modal primitive ----------
function Modal({ onClose, title, subtitle, width=560, children, footer }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-ink-900/50 backdrop-blur-[2px]" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-pop modal-enter flex flex-col max-h-[90vh]" style={{ width }}>
        <div className="px-6 py-4 border-b border-line flex items-start justify-between gap-4">
          <div className="min-w-0">
            {subtitle && <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold">{subtitle}</div>}
            <h3 className="text-lg font-bold text-ink-900 mt-0.5 truncate">{title}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-surface flex items-center justify-center text-ink-500 shrink-0"><Icon name="x" size={16}/></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto scroll-thin px-6 py-5">{children}</div>
        {footer && <div className="px-6 py-3 border-t border-line bg-surface flex items-center justify-end gap-2 rounded-b-2xl">{footer}</div>}
      </div>
    </div>
  );
}

// ---------- Form primitives ----------
function Label({ children, required }) {
  return <label className="block text-[11.5px] font-semibold text-ink-700 mb-1.5">{children}{required && <span className="text-bad ml-0.5">*</span>}</label>;
}
function FormGroup({ label, required, children, hint, error, cols=1 }) {
  return (
    <div className={cols===2 ? 'col-span-2' : ''}>
      {label && <Label required={required}>{label}</Label>}
      {children}
      {hint && <div className="text-[11px] text-ink-500 mt-1">{hint}</div>}
      {error && <div className="text-[11px] text-bad mt-1">{error}</div>}
    </div>
  );
}
function Select({ value, onChange, options, placeholder='Seleccionar…', className='' }) {
  return (
    <select value={value} onChange={e=>onChange(e.target.value)} className={cx('inp w-full cursor-pointer', className)}>
      <option value="" disabled>{placeholder}</option>
      {options.map(o => (
        <option key={typeof o==='string'?o:o.value} value={typeof o==='string'?o:o.value}>
          {typeof o==='string'?o:o.label}
        </option>
      ))}
    </select>
  );
}

// ---------- Buscador de clientes ----------
// Reemplaza al <select> de 3.000 clientes, que solo saltaba por las primeras letras (MYS-0029).
// `value` y `onChange` usan el código del cliente, como el select de antes.
// `compacto`: para las barras de filtros (sin el renglón de datos debajo y con la lista más ancha que la columna).
function BuscadorCliente({ clients, value, onChange, placeholder = 'Buscar por nombre, CUIT o código…', compacto = false }) {
  const [texto, setTexto] = useS('');
  const [abierto, setAbierto] = useS(false);
  const elegido = clients.find(c => c.code === value) || null;
  const corto = texto.trim().length < 2;
  const lista = corto ? [] : clients.filter(c => coincideBusqueda(texto, c.name, c.cuit, c.code, c.email, c.city)).slice(0, 60);
  const elegir = (c) => { onChange(c.code); setTexto(''); setAbierto(false); };
  return (
    <div className="relative">
      <div className="relative">
        <Icon name="search" size={compacto ? 12 : 14} className={cx('absolute top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none', compacto ? 'left-2' : 'left-3')}/>
        <input className={cx('inp w-full', compacto ? 'pl-6 pr-6 h-[26px] text-[13px]' : 'pl-9 pr-8')} placeholder={placeholder}
          value={abierto ? texto : (elegido ? elegido.name : '')}
          onFocus={() => { setAbierto(true); setTexto(''); }}
          onChange={e => { setTexto(e.target.value); setAbierto(true); }}
          onKeyDown={e => { if (e.key === 'Escape') setAbierto(false); if (e.key === 'Enter' && lista.length === 1) { e.preventDefault(); elegir(lista[0]); } }}/>
        {elegido && !abierto && (
          <button type="button" title="Quitar" onClick={() => onChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-ink-400 hover:text-ink-700 hover:bg-surface flex items-center justify-center">
            <Icon name="x" size={13}/>
          </button>
        )}
      </div>
      {elegido && !abierto && !compacto && (
        <div className="text-[11.5px] text-ink-500 mt-1 truncate">
          {[elegido.code, elegido.cuit && `CUIT ${elegido.cuit}`, [elegido.city, elegido.prov].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
        </div>
      )}
      {abierto && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setAbierto(false)}/>
          <div className={cx('absolute z-20 mt-1 bg-white border border-line rounded-xl shadow-pop max-h-64 overflow-y-auto scroll-thin', compacto ? 'w-[340px] max-w-[85vw]' : 'w-full')}>
            {corto ? (
              <div className="px-3 py-3 text-[12.5px] text-ink-400 text-center">Escribí al menos 2 letras: nombre, CUIT, código o ciudad</div>
            ) : lista.length === 0 ? (
              <div className="px-3 py-3 text-[12.5px] text-ink-400 text-center">Ningún cliente coincide</div>
            ) : lista.map(c => (
              <button type="button" key={c.id || c.code} onClick={() => elegir(c)}
                className={cx('w-full text-left px-3 py-2 border-b border-line last:border-b-0 hover:bg-surface', c.code === value && 'bg-brandSoft/40')}>
                <div className="text-[12.5px] font-medium text-ink-900 truncate">{c.name}</div>
                <div className="text-[11px] text-ink-500 truncate">
                  {[c.code, c.cuit && `CUIT ${c.cuit}`, [c.city, c.prov].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                </div>
              </button>
            ))}
            {!corto && lista.length === 60 && (
              <div className="px-3 py-2 text-[11px] text-ink-400 text-center">Se muestran los primeros 60: agregá otra palabra para afinar</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Modal Host ----------
function ModalHost() {
  const { modals } = useApp();
  return (
    <>
      {modals.map((m, i) => {
        const K = MODAL_REGISTRY[m.kind];
        if (!K) return null;
        return <K key={i} {...m.props}/>;
      })}
    </>
  );
}

// ================================================================
// ----------- MODALS ---------------------------------------------
// ================================================================

// --- 1. Nueva Cotización ---
function NewQuoteModal({ defaultClient }) {
  const { closeModal, addQuote, clients, users, setQuotes, pushToast, currentUserId } = useApp();
  // Paso 0: elegir qué se está cargando. Una Solicitud es un pedido que entró por
  // otro canal (teléfono, WhatsApp, web) y todavía hay que cotizar; un Presupuesto
  // es uno que el vendedor ya armó y mandó por fuera del CRM.
  const [tipo, setTipo] = useS(null);
  const esSolicitud = tipo === 'SOLICITUD';
  const [form, setForm] = useS({
    client: defaultClient || '',
    seller: currentUserId || '',
    ingreso: hoyInput(),
    monto: '',
    currency: 'USD',
    origin: 'Mail',
    observaciones: '',
    pedido: '',
    pedidoDe: '',
    pedidoRef: '',
  });
  const [saving, setSaving] = useS(false);
  const [parsing, setParsing] = useS(false);
  const [pdfFile, setPdfFile] = useS(null);
  const [parseResult, setParseResult] = useS(null);
  const fileRef = React.useRef();
  const set = (k,v) => setForm(f => ({...f, [k]: v}));
  // En una solicitud el vendedor es opcional: puede entrar "Sin asignar" igual que
  // las que llegan por mail, y después el vendedor se la auto-asigna.
  const canSubmit = form.client && (esSolicitud || form.seller);

  const handleFile = async (file) => {
    if (!file) return;
    setPdfFile(file);
    setParseResult(null);
    // El parser lee presupuestos de Flexxus — en una solicitud el PDF es del
    // cliente, así que se adjunta sin intentar interpretarlo.
    if (esSolicitud) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) return;
    setParsing(true);
    try {
      const data = await CrmApi.parsePresupuesto(file);
      setParseResult(data);
      if (data.client) {
        set('client', data.client.code);
        if (data.defaultSeller) set('seller', data.defaultSeller.id);
      }
      if (data.total) set('monto', String(data.total));
      pushToast(`PDF parseado: ${data.flexxusCode || '—'} · ${data.itemCount} ítem${data.itemCount !== 1 ? 's' : ''}`);
    } catch (err) {
      pushToast(err.message || 'No se pudo parsear el PDF', 'bad');
    } finally {
      setParsing(false);
    }
  };

  const clearFile = () => {
    setPdfFile(null);
    setParseResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    const client = clients.find(c => c.code === form.client);
    const source = SOURCE_MAP[form.origin] || 'MANUAL';
    setSaving(true);
    try {
      const created = await CrmApi.createQuote({
        type: tipo,
        clientId: client?.id || null,
        sellerId: form.seller || null,
        amount: esSolicitud ? null : (form.monto ? parseFloat(form.monto) : null),
        currency: form.currency,
        source,
        ingreso: form.ingreso || null,
        // En una solicitud el texto del cliente va al cuerpo del pedido (se ve
        // como el mail); las Observaciones son del presupuesto y son una nota.
        notes: esSolicitud ? null : (form.observaciones || null),
        ...(esSolicitud ? { pedido: form.pedido || null, pedidoDe: form.pedidoDe || null, pedidoRef: form.pedidoRef || null } : {}),
      });
      if (pdfFile && created?.id) {
        try {
          await CrmApi.uploadAttachments(created.id, [pdfFile]);
        } catch {
          pushToast('Cotización creada, pero falló la subida del archivo', 'bad');
        }
      }
      const freshQuotes = await CrmApi.getQuotes();
      setQuotes(freshQuotes);
      pushToast(`${esSolicitud ? 'Solicitud' : 'Presupuesto'} ${created?.code || ''} creada correctamente`);
      closeModal();
    } catch (err) {
      pushToast(err.message || 'Error al crear', 'bad');
    } finally {
      setSaving(false);
    }
  };

  // ── Paso 0: qué querés cargar ──
  if (!tipo) {
    const opciones = [
      { id: 'SOLICITUD',   icon: 'inbox',     titulo: 'Solicitud de cotización',
        desc: 'Un pedido que entró por teléfono, WhatsApp, web o licitación y todavía hay que cotizar.',
        pie: 'Entra al tablero como Solicitud, con fecha límite de armado.' },
      { id: 'PRESUPUESTO', icon: 'file-text', titulo: 'Presupuesto ya enviado',
        desc: 'Un presupuesto que el vendedor ya armó y le mandó al cliente por fuera del CRM.',
        pie: 'Entra directo en la etapa Enviado, con seguimiento automático.' },
    ];
    return (
      <Modal onClose={closeModal} title="Nuevo" subtitle="¿Qué querés cargar?" width={620}
        footer={<button className="btn-ghost" onClick={closeModal}>Cancelar</button>}>
        <div className="space-y-3">
          {opciones.map(o => (
            <button key={o.id} onClick={() => setTipo(o.id)}
              className="w-full text-left flex items-start gap-4 p-4 rounded-xl border-2 border-line hover:border-brand hover:bg-brandSoft/15 transition-colors">
              <span className="shrink-0 w-10 h-10 rounded-lg bg-brandSoft/40 flex items-center justify-center text-brand">
                <Icon name={o.icon} size={19}/>
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold text-ink-900">{o.titulo}</span>
                <span className="block text-[12.5px] text-ink-600 mt-0.5 leading-snug">{o.desc}</span>
                <span className="block text-[11.5px] text-ink-400 mt-1.5">{o.pie}</span>
              </span>
            </button>
          ))}
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={closeModal}
      subtitle={esSolicitud ? 'Fase 1 · Pedido a cotizar' : 'Fase 1 · Presupuesto ya armado y enviado'}
      title={esSolicitud ? 'Nueva solicitud' : 'Nuevo presupuesto'} width={620}
      footer={
        <>
          <button className="btn-ghost" onClick={() => setTipo(null)} disabled={saving}>← Volver</button>
          <button className="btn-ghost" onClick={closeModal} disabled={saving}>Cancelar</button>
          <button className="btn-primary" disabled={!canSubmit || saving || parsing} onClick={submit}
            style={!canSubmit || saving || parsing ? {opacity:.45, cursor:'not-allowed'} : {}}>
            <Icon name={saving ? 'loader' : 'plus'} size={13}/>
            {saving ? 'Guardando…' : esSolicitud ? 'Crear solicitud' : 'Crear presupuesto'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">

        {/* ── Upload PDF (primero) ── */}
        <div className="col-span-2">
          <label className="block text-[11px] font-semibold text-ink-500 uppercase tracking-wide mb-1.5">
            {esSolicitud ? 'Adjuntar archivo' : 'Adjuntar PDF Flexxus'}
            <span className="font-normal text-ink-400 normal-case">
              {esSolicitud ? ' (opcional — el pedido del cliente)' : ' (opcional — auto-completa los campos)'}
            </span>
          </label>
          <div
            onClick={() => fileRef.current?.click()}
            className={cx(
              'flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
              pdfFile ? 'border-brand bg-brandSoft/20' : 'border-line hover:border-brand/50 hover:bg-surface'
            )}
          >
            <Icon name={parsing ? 'loader' : pdfFile ? 'file-check' : 'upload'} size={18}
              className={cx(parsing ? 'animate-spin text-brand' : pdfFile ? 'text-brand' : 'text-ink-400')}/>
            <div className="flex-1 min-w-0">
              {parsing ? (
                <span className="text-[13px] text-brand font-medium">Procesando PDF…</span>
              ) : pdfFile ? (
                <span className="text-[13px] text-ink-700 font-medium truncate">{pdfFile.name}</span>
              ) : (
                <span className="text-[13px] text-ink-400">
                  {esSolicitud ? 'Hacé clic para adjuntar el pedido del cliente' : 'Hacé clic para subir un PDF de presupuesto'}
                </span>
              )}
            </div>
            {pdfFile && !parsing && (
              <button onClick={e => { e.stopPropagation(); clearFile(); }}
                className="text-ink-400 hover:text-red-500 transition-colors">
                <Icon name="x" size={14}/>
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.doc,.docx,.csv,.jpg,.jpeg,.png,.gif,.webp" className="hidden"
            onChange={e => handleFile(e.target.files[0])}/>
        </div>

        {/* ── Resultado del parse ── */}
        {parseResult && (
          <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 text-[12px] text-emerald-800 space-y-0.5">
            <div className="font-semibold flex items-center gap-1.5"><Icon name="check-circle" size={13}/>PDF procesado correctamente</div>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-emerald-700">
              {parseResult.flexxusCode && <span>Código: <b>{parseResult.flexxusCode}</b></span>}
              {parseResult.clientName && <span>Cliente: <b>{parseResult.clientName}</b></span>}
              {parseResult.client && <span className="text-emerald-600">→ encontrado en CRM</span>}
              {parseResult.total != null && <span>Total: <b>U$S {parseResult.total.toLocaleString('es-AR',{minimumFractionDigits:2})}</b></span>}
              {parseResult.itemCount > 0 && <span>{parseResult.itemCount} ítems</span>}
            </div>
          </div>
        )}

        {/* ── Warning: cliente no encontrado ── */}
        {parseResult && parseResult.cuit && !parseResult.client && (
          <div className="col-span-2 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-[12px] text-amber-800">
            <Icon name="alert-triangle" size={13} className="text-amber-500 shrink-0"/>
            Cliente <b className="mono">{parseResult.cuit}</b>{parseResult.clientName ? ` (${parseResult.clientName})` : ''} no encontrado en el CRM — seleccionalo manualmente abajo
          </div>
        )}

        {/* ── Cliente ── */}
        <FormGroup label="Cliente" required cols={2}>
          <BuscadorCliente clients={clients} value={form.client} onChange={v=>set('client',v)}/>
        </FormGroup>
        <FormGroup label="Vendedor asignado" required={!esSolicitud}
          hint={esSolicitud ? 'Opcional — si lo dejás vacío queda "Sin asignar"' : undefined}>
          <Select value={form.seller} onChange={v=>set('seller',v)}
            placeholder={esSolicitud ? 'Sin asignar' : undefined}
            options={users.filter(u=>u.role==='Vendedor'||u.role==='Administrador').map(u => ({ value:u.id, label:u.name }))}/>
        </FormGroup>
        <FormGroup label="Origen">
          <Select value={form.origin} onChange={v=>set('origin',v)} options={ORIGINS}/>
        </FormGroup>
        <FormGroup label="Fecha de ingreso" cols={2}>
          <input type="date" className="inp w-full" value={form.ingreso} onChange={e=>set('ingreso',e.target.value)}/>
        </FormGroup>
        {!esSolicitud && <FormGroup label="Monto" hint="Opcional" cols={2}>
          <div className="flex gap-2">
            <select className="inp w-24 shrink-0" value={form.currency} onChange={e=>set('currency',e.target.value)}>
              <option value="USD">U$S</option>
              <option value="ARS">AR$</option>
            </select>
            <input type="number" className="inp flex-1" placeholder="Ej: 45200" value={form.monto} onChange={e=>set('monto',e.target.value)}/>
          </div>
        </FormGroup>}
        {esSolicitud ? (
          <>
            <FormGroup label="Referencia" hint="Opcional — como el asunto de un mail">
              <input className="inp w-full" placeholder="Ej: Pedido cables 3x2.5"
                value={form.pedidoRef} onChange={e=>set('pedidoRef',e.target.value)}/>
            </FormGroup>
            <FormGroup label="De" hint="Opcional — quién lo pidió">
              <input className="inp w-full" placeholder="Ej: Juan Pérez · 351 555 1234"
                value={form.pedidoDe} onChange={e=>set('pedidoDe',e.target.value)}/>
            </FormGroup>
            <FormGroup label="Pedido del cliente" cols={2}>
              <textarea rows="6" className="inp w-full resize-none"
                placeholder="Pegá acá lo que te escribió el cliente por WhatsApp, o escribí lo que te pidió por teléfono…"
                value={form.pedido} onChange={e=>set('pedido',e.target.value)}/>
              <p className="text-[11px] text-ink-400 mt-1">Se muestra en la ficha igual que el cuerpo de un mail. Después lo podés editar.</p>
            </FormGroup>
          </>
        ) : (
          <FormGroup label="Observaciones" cols={2}>
            <textarea rows="3" className="inp w-full resize-none" placeholder="Contexto del pedido, urgencia, condiciones particulares…"
              value={form.observaciones} onChange={e=>set('observaciones',e.target.value)}/>
            <p className="text-[11px] text-ink-400 mt-1">Se guarda como la primera nota del caso.</p>
          </FormGroup>
        )}
      </div>
    </Modal>
  );
}

// --- 3. Nuevo Cliente ---
function NewClientModal() {
  const { closeModal, users, setClients, pushToast } = useApp();
  const [form, setForm] = useS({
    name:'', cuit:'', address:'', city:'', prov:'', zone:'', cp:'', activity:'', phone:'', email:'', seller:''
  });
  const [saving, setSaving] = useS(false);
  const set = (k,v) => setForm(f => ({...f, [k]:v}));
  const canSubmit = form.name;

  const submit = async () => {
    setSaving(true);
    try {
      await CrmApi.createClient({
        name: form.name,
        cuit: form.cuit || null,
        email: form.email || null,
        phone: form.phone || null,
        address: form.address || null,
        city: form.city || null,
        province: form.prov || null,
        zone: form.zone || null,
        activity: form.activity || null,
        defaultSellerId: form.seller || null,
      });
      const clients = await CrmApi.getClients();
      const mapped = clients.map(c => ({
        id: c.id, code: c.code, name: c.name, cuit: c.cuit || '',
        city: c.city || '', prov: c.province || '', zone: c.zone || '',
        activity: c.activity || '', seller: c.defaultSellerId || '',
        sellerName: c.defaultSeller?.name || '', legacySellerName: c.legacySellerName || '',
        email: c.email || '',
        phone: c.phone || '', address: c.address || '',
      }));
      setClients(mapped);
      pushToast('Cliente creado correctamente');
      closeModal();
    } catch (err) {
      pushToast(err.message || 'Error al crear cliente', 'bad');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={closeModal} subtitle="Directorio de clientes" title="Nuevo cliente" width={680}
      footer={
        <>
          <button className="btn-ghost" onClick={closeModal} disabled={saving}>Cancelar</button>
          <button className="btn-primary" disabled={!canSubmit || saving} onClick={submit}
            style={!canSubmit || saving ? {opacity:.45, cursor:'not-allowed'} : {}}>
            <Icon name="check" size={13}/>{saving ? 'Guardando...' : 'Guardar cliente'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <FormGroup label="Razón social" required cols={2}>
          <input className="inp w-full" placeholder="Ej: ARGENCRAFT S.A." value={form.name} onChange={e=>set('name',e.target.value)}/>
        </FormGroup>
        <FormGroup label="CUIT" required>
          <input className="inp w-full mono" placeholder="30-12345678-9" value={form.cuit} onChange={e=>set('cuit',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Código postal">
          <input className="inp w-full mono" placeholder="1629" value={form.cp} onChange={e=>set('cp',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Dirección" cols={2}>
          <input className="inp w-full" placeholder="Calle, altura, piso" value={form.address} onChange={e=>set('address',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Localidad">
          <input className="inp w-full" placeholder="Ciudad / Partido" value={form.city} onChange={e=>set('city',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Provincia">
          <Select value={form.prov} onChange={v=>set('prov',v)} options={PROVINCES}/>
        </FormGroup>
        <FormGroup label="Zona comercial">
          <Select value={form.zone} onChange={v=>set('zone',v)} options={ZONES}/>
        </FormGroup>
        <FormGroup label="Tipo de actividad">
          <Select value={form.activity} onChange={v=>set('activity',v)} options={ACTIVITIES}/>
        </FormGroup>
        <FormGroup label="Teléfono">
          <input className="inp w-full mono" placeholder="+54 11 0000-0000" value={form.phone} onChange={e=>set('phone',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Email">
          <input className="inp w-full" placeholder="compras@empresa.com" value={form.email} onChange={e=>set('email',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Vendedor asignado" cols={2}>
          <Select value={form.seller} onChange={v=>set('seller',v)}
            options={users.filter(u=>u.role==='Vendedor'||u.role==='Administrador').map(u => ({ value:u.id, label:`${u.name} · ${u.zone}` }))}/>
        </FormGroup>
      </div>
    </Modal>
  );
}

// --- 4. Editar Cliente ---
function EditClientModal({ clientId }) {
  const { closeModal, clients, users, setClients, pushToast } = useApp();
  const source = clients.find(c => c.id === clientId);
  const [form, setForm] = useS({
    name: source?.name || '',
    cuit: source?.cuit || '',
    address: source?.address || '',
    city: source?.city || '',
    prov: source?.prov || '',
    zone: source?.zone || '',
    cp: '',
    activity: source?.activity || '',
    phone: source?.phone || '',
    email: source?.email || '',
    seller: source?.seller || '',
  });
  const [saving, setSaving] = useS(false);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const canSubmit = !!form.name;

  // ── Emails de matcheo ──
  const [clientEmails, setClientEmails] = useS([]);
  const [newEmail, setNewEmail] = useS('');
  const [emailSaving, setEmailSaving] = useS(false);

  React.useEffect(() => {
    if (!clientId) return;
    CrmApi.getClientEmails(clientId).then(setClientEmails).catch(() => {});
  }, [clientId]);

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;
    setEmailSaving(true);
    try {
      const record = await CrmApi.addClientEmail(clientId, newEmail.trim());
      setClientEmails(prev => [...prev, record]);
      setNewEmail('');
      pushToast('Email agregado');
    } catch (err) {
      pushToast(err.message || 'Error al agregar email', 'bad');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleRemoveEmail = async (emailId) => {
    try {
      await CrmApi.removeClientEmail(clientId, emailId);
      setClientEmails(prev => prev.filter(e => e.id !== emailId));
      pushToast('Email eliminado');
    } catch (err) {
      pushToast('Error al eliminar email', 'bad');
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await CrmApi.updateClient(clientId, {
        name: form.name,
        cuit: form.cuit || null,
        email: form.email || null,
        phone: form.phone || null,
        address: form.address || null,
        city: form.city || null,
        province: form.prov || null,
        zone: form.zone || null,
        activity: form.activity || null,
        defaultSellerId: form.seller || null,
      });
      const updated = await CrmApi.getClients();
      const mapped = updated.map(c => ({
        id: c.id, code: c.code, name: c.name, cuit: c.cuit || '',
        city: c.city || '', prov: c.province || '', zone: c.zone || '',
        activity: c.activity || '', seller: c.defaultSellerId || '',
        sellerName: c.defaultSeller?.name || '', legacySellerName: c.legacySellerName || '',
        email: c.email || '',
        phone: c.phone || '', address: c.address || '',
      }));
      setClients(mapped);
      pushToast('Cliente actualizado correctamente');
      closeModal();
    } catch (err) {
      pushToast(err.message || 'Error al actualizar cliente', 'bad');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={closeModal} subtitle="Directorio de clientes" title="Editar cliente" width={680}
      footer={
        <>
          <button className="btn-ghost" onClick={closeModal} disabled={saving}>Cancelar</button>
          <button className="btn-primary" disabled={!canSubmit || saving} onClick={submit}
            style={!canSubmit || saving ? {opacity:.45, cursor:'not-allowed'} : {}}>
            <Icon name="check" size={13}/>{saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <FormGroup label="Razón social" required cols={2}>
          <input className="inp w-full" value={form.name} onChange={e=>set('name',e.target.value)}/>
        </FormGroup>
        <FormGroup label="CUIT">
          <input className="inp w-full mono" value={form.cuit} onChange={e=>set('cuit',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Código postal">
          <input className="inp w-full mono" value={form.cp} onChange={e=>set('cp',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Dirección" cols={2}>
          <input className="inp w-full" value={form.address} onChange={e=>set('address',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Localidad">
          <input className="inp w-full" value={form.city} onChange={e=>set('city',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Provincia">
          <Select value={form.prov} onChange={v=>set('prov',v)} options={PROVINCES}/>
        </FormGroup>
        <FormGroup label="Zona comercial">
          <Select value={form.zone} onChange={v=>set('zone',v)} options={ZONES}/>
        </FormGroup>
        <FormGroup label="Tipo de actividad">
          <Select value={form.activity} onChange={v=>set('activity',v)} options={ACTIVITIES}/>
        </FormGroup>
        <FormGroup label="Teléfono">
          <input className="inp w-full mono" value={form.phone} onChange={e=>set('phone',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Email principal">
          <input className="inp w-full" value={form.email} onChange={e=>set('email',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Vendedor asignado" cols={2}>
          <Select value={form.seller} onChange={v=>set('seller',v)}
            options={users.filter(u=>u.role==='Vendedor'||u.role==='Administrador').map(u => ({ value:u.id, label:`${u.name} · ${u.zone}` }))}/>
        </FormGroup>

        {/* ── Emails de matcheo automático ── */}
        <div className="col-span-2 pt-2 border-t border-line">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-2">
            Emails de matcheo automático
          </div>
          <div className="text-[11.5px] text-ink-400 mb-3">
            Cuando llegue un mail de alguno de estos remitentes, se vincula automáticamente a este cliente.
          </div>
          {/* Lista de emails registrados */}
          <div className="space-y-1.5 mb-3">
            {clientEmails.length === 0 && (
              <div className="text-[12px] text-ink-400 py-2">Sin emails adicionales registrados</div>
            )}
            {clientEmails.map(e => (
              <div key={e.id} className="flex items-center justify-between bg-surface border border-line rounded-lg px-3 py-2">
                <span className="text-[12.5px] text-ink-900 mono">{e.email}</span>
                <button onClick={() => handleRemoveEmail(e.id)}
                  className="w-6 h-6 rounded hover:bg-red-50 text-ink-400 hover:text-bad flex items-center justify-center">
                  <Icon name="x" size={12}/>
                </button>
              </div>
            ))}
          </div>
          {/* Agregar nuevo email */}
          <div className="flex gap-2">
            <input className="inp flex-1 text-xs mono" placeholder="nuevo@email.com"
              value={newEmail} onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddEmail()}/>
            <button className="btn-ghost text-xs" onClick={handleAddEmail}
              disabled={!newEmail.trim() || emailSaving}>
              <Icon name="plus" size={12}/>{emailSaving ? 'Agregando…' : 'Agregar'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// --- 5. Invitar Usuario ---
function InviteUserModal() {
  const { closeModal, pushToast, setUsers } = useApp();
  const [form, setForm] = useS({ name:'', email:'', role:'VENDEDOR', zone:'' });
  const [loading, setLoading] = useS(false);
  const [error, setError] = useS('');
  const set = (k,v) => setForm(f => ({...f,[k]:v}));
  const canSubmit = form.name && form.email && !loading;

  const roleOptions = [
    { value:'VENDEDOR',  label:'Vendedor',      icon:'briefcase',    desc:'Gestiona cotizaciones y clientes' },
    { value:'LOGISTICA', label:'Logística',      icon:'truck',        desc:'Consulta de clientes y Foro (solo lectura)' },
    { value:'ADMIN',     label:'Administrador',  icon:'shield',       desc:'Acceso completo al sistema' },
  ];

  const submit = async () => {
    setLoading(true); setError('');
    try {
      const payload = { name: form.name.trim(), email: form.email.trim().toLowerCase(), role: form.role };
      if (form.role === 'VENDEDOR' && form.zone) payload.zone = form.zone;
      const created = await CrmApi.createUser(payload);
      // Actualizar lista de users en context
      setUsers(us => [...us, { id: created.id, name: created.name, email: created.email, role: created.role, zone: created.zone }]);
      pushToast(`Usuario creado. Se envió mail a ${created.email}`);
      closeModal();
    } catch (err) {
      setError(err.message || 'Error al crear usuario');
      setLoading(false);
    }
  };

  return (
    <Modal onClose={closeModal} subtitle="Equipo" title="Invitar usuario" width={520}
      footer={
        <>
          <button className="btn-ghost" onClick={closeModal}>Cancelar</button>
          <button className="btn-primary" disabled={!canSubmit} onClick={submit} style={!canSubmit?{opacity:.45, cursor:'not-allowed'}:{}}>
            <Icon name="send" size={13}/>{loading ? 'Enviando...' : 'Crear e invitar'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FormGroup label="Nombre completo" required>
          <input className="inp w-full" placeholder="Ej: Camila Ferrari" value={form.name} onChange={e=>set('name',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Email corporativo" required>
          <input type="email" className="inp w-full" placeholder="nombre@myselec.com.ar" value={form.email} onChange={e=>set('email',e.target.value)}/>
        </FormGroup>
        <FormGroup label="Rol">
          <div className="flex flex-col gap-2">
            {roleOptions.map(r => (
              <label key={r.value} className={cx('flex items-center gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-colors',
                form.role===r.value ? 'border-brand bg-blue-50' : 'border-line bg-white hover:bg-surface')}>
                <input type="radio" checked={form.role===r.value} onChange={()=>set('role',r.value)} className="accent-brand"/>
                <Icon name={r.icon} size={16} className={form.role===r.value ? 'text-brand' : 'text-ink-400'}/>
                <div>
                  <div className="text-[13px] font-medium">{r.label}</div>
                  <div className="text-[11px] text-ink-500">{r.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </FormGroup>
        {form.role === 'VENDEDOR' && (
          <FormGroup label="Zona comercial (opcional)">
            <Select value={form.zone} onChange={v=>set('zone',v)} options={ZONES} placeholder="Sin asignar"/>
          </FormGroup>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-[12px] text-red-700">
            <Icon name="alert-circle" size={12} className="inline mr-1.5"/>{error}
          </div>
        )}
        <div className="bg-brandSoft/40 border border-brand/20 rounded-lg px-3 py-2.5 text-[12px] text-navy-900">
          <Icon name="info" size={12} className="inline mr-1.5"/>
          Se enviará un mail a <b>{form.email || 'destinatario'}</b> con un enlace para configurar su contraseña (válido por 48 hs).
        </div>
      </div>
    </Modal>
  );
}

// --- 5. Permisos (solo lectura estructurada) ---
function PermissionsModal() {
  const { closeModal } = useApp();
  const rows = [
    ['Dashboard',            {a:'Ver', v:'—', l:'—'}],
    ['Cotizaciones',         {a:'Ver + editar', v:'Propias', l:'—'}],
    ['Notas de Pedido',      {a:'Ver + editar', v:'Propias', l:'Avanzar etapa'}],
    ['Clientes',             {a:'Gestionar', v:'Ver', l:'—'}],
    ['Equipo',               {a:'Gestionar', v:'—', l:'—'}],
    ['Configuración',        {a:'Gestionar', v:'—', l:'—'}],
    ['Reportes y Export',    {a:'Completo', v:'Propios', l:'Entregas'}],
    ['Integración Flexxus',  {a:'Configurar', v:'Consultar', l:'Sincronizar'}],
  ];
  const cell = (v) => {
    if (v === '—') return <span className="text-ink-300">—</span>;
    const tone = v.includes('Gestionar')||v.includes('Completo')||v.includes('Configurar')?'navy':v.includes('Propi')?'blue':v==='Ver'?'slate':'green';
    return <Badge tone={tone}>{v}</Badge>;
  };
  return (
    <Modal onClose={closeModal} subtitle="Equipo" title="Matriz de permisos por rol" width={720}
      footer={<button className="btn-ghost" onClick={closeModal}>Cerrar</button>}
    >
      <div className="bg-white border border-line rounded-xl overflow-hidden">
        <table className="tbl w-full">
          <thead><tr>
            <th>Módulo</th>
            <th className="!text-center">Admin</th>
            <th className="!text-center">Vendedor</th>
            <th className="!text-center">Logística</th>
          </tr></thead>
          <tbody>
            {rows.map(([label, {a,v,l}]) => (
              <tr key={label}>
                <td className="font-medium">{label}</td>
                <td className="!text-center">{cell(a)}</td>
                <td className="!text-center">{cell(v)}</td>
                <td className="!text-center">{cell(l)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11.5px] text-ink-500 mt-3">
        Los cambios en esta matriz requieren confirmación del administrador principal y se auditan en el historial del sistema.
      </div>
    </Modal>
  );
}

// --- 6. Búsqueda global ---
function SearchPaletteModal() {
  const { closeModal, quotes, clients, openModal } = useApp();
  const [q, setQ] = useS('');
  const [productResults, setProductResults] = useS([]);
  const [productLoading, setProductLoading] = useS(false);
  const query = q.trim();

  // Búsqueda de productos (debounced via backend)
  useEff(() => {
    if (q.trim().length < 2) { setProductResults([]); return; }
    const timer = setTimeout(() => {
      setProductLoading(true);
      CrmApi.searchProducts(q.trim()).then(r => { setProductResults(r || []); setProductLoading(false); })
        .catch(() => { setProductResults([]); setProductLoading(false); });
    }, 350);
    return () => clearTimeout(timer);
  }, [q]);

  const matchedQuotes = !query ? [] :
    quotes.filter(x => coincideBusqueda(query, x.code, x.clientName, x.flexxus, x.emailSubject)).slice(0,6);
  const matchedClients = !query ? [] :
    clients.filter(c => coincideBusqueda(query, c.name, c.cuit, c.code, c.email)).slice(0,5);
  const hasResults = matchedQuotes.length + matchedClients.length + productResults.length > 0;

  const go = (ref) => {
    closeModal();
    openModal('quoteDetail', { code: ref.code });
  };

  const goClient = (c) => {
    closeModal();
    setTimeout(() => openModal('editClient', { clientId: c.id }), 50);
  };

  const typeLabel = (mt) => mt === 'NOTA_PEDIDO' ? 'NP' : mt === 'PRESUPUESTO' ? 'Pres.' : 'Cot.';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 p-6">
      <div className="absolute inset-0 modal-overlay" onClick={closeModal}/>
      <div className="relative bg-white rounded-2xl shadow-pop modal-enter w-[640px] max-w-full overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-line">
          <Icon name="search" size={18} className="text-ink-400"/>
          <input autoFocus value={q} onChange={e=>setQ(e.target.value)}
            placeholder="Buscar cotizaciones, clientes, productos…"
            className="flex-1 outline-none bg-transparent text-[15px] placeholder:text-ink-400"/>
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-line text-ink-500 bg-surface">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto scroll-thin">
          {matchedQuotes.length > 0 && (
            <div className="p-2">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-ink-500">Cotizaciones</div>
              {matchedQuotes.map(x => {
                const c = clients.find(y=>y.code===x.client);
                const stg = STAGES_F1.find(s=>s.id===x.stage);
                return (
                  <button key={x.code} onClick={()=>go({kind:'quote', code:x.code})}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface text-left">
                    <Icon name="clipboard-list" size={15} className="text-ink-500"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold truncate"><span className="mono text-navy-900">{x.code}</span> — {c?.name}</div>
                      <div className="text-[11px] text-ink-500">{c?.city}{c?.city&&x.monto?' · ':''}{x.monto?fmtMoney(x.monto, x.currency):''}</div>
                    </div>
                    {stg && <Badge tone={stg.tone} dot>{stg.label}</Badge>}
                  </button>
                );
              })}
            </div>
          )}
          {matchedClients.length > 0 && (
            <div className="p-2 border-t border-line">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-ink-500">Clientes</div>
              {matchedClients.map(c => (
                <button key={c.code} onClick={() => goClient(c)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface text-left">
                  <Icon name="building-2" size={15} className="text-ink-500"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold truncate">{c.name}</div>
                    <div className="text-[11px] text-ink-500">{[c.city, c.prov].filter(Boolean).join(', ')}{c.cuit ? ` · ${c.cuit}` : ''}</div>
                  </div>
                  <span className="mono text-[11px] text-ink-400">{c.code}</span>
                </button>
              ))}
            </div>
          )}
          {/* Productos (búsqueda backend) */}
          {productResults.length > 0 && (
            <div className="p-2 border-t border-line">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-ink-500 flex items-center gap-2">
                Productos <span className="text-ink-300 font-normal">({productResults.length} cotización{productResults.length!==1?'es':''})</span>
              </div>
              {productResults.slice(0,8).map(p => {
                const stg = STAGES_F1.find(s=>s.id===p.stage);
                return (
                  <button key={p.quoteId} onClick={()=>go({kind:'quote', code:p.quoteCode})}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface text-left">
                    <Icon name="box" size={15} className="text-amber-600"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold truncate">
                        {p.matchedSku && <span className="mono text-amber-700">{p.matchedSku}</span>}
                        {p.matchedSku && ' — '}
                        <span className="text-ink-700">{p.matchedDesc}</span>
                      </div>
                      <div className="text-[11px] text-ink-500">
                        {typeLabel(p.mailType)} <span className="mono">{p.quoteCode}</span> · {p.clientName}
                        {p.matchedQty > 1 ? ` · ×${p.matchedQty}` : ''}
                      </div>
                    </div>
                    {stg && <Badge tone={stg.tone} dot>{stg.label}</Badge>}
                  </button>
                );
              })}
            </div>
          )}
          {productLoading && query && (
            <div className="px-5 py-2 text-[11px] text-ink-400 flex items-center gap-2">
              <div className="animate-spin rounded-full h-3 w-3 border border-brand border-t-transparent"/>
              Buscando productos…
            </div>
          )}
          {!query && (
            <div className="py-10 text-center space-y-1">
              <Icon name="search" size={22} className="text-ink-300 mx-auto"/>
              <div className="text-[13px] text-ink-400 mt-2">Buscá por código, cliente, NP, asunto o producto</div>
              <div className="text-[11px] text-ink-300">Cotizaciones · Clientes · Productos</div>
            </div>
          )}
          {query && !hasResults && !productLoading && (
            <div className="py-12 text-center text-ink-500 text-[13px]">
              Sin resultados para "<b>{q}</b>"
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Detail modal wrappers (route to existing drawers via registry) ---
function QuoteDetailWrapper({ code }) {
  const { closeModal, roleKey } = useApp();
  return <QuoteDetail code={code} onClose={closeModal} canReassign={['admin','seller'].includes(roleKey)}/>;
}
// ---------- ClientDetail — timeline de un cliente ----------
function ClientDetailModal({ clientId }) {
  const { closeModal, openModal } = useApp();
  const [data, setData] = useS(null);
  const [loading, setLoading] = useS(true);

  useEff(() => {
    if (!clientId) return;
    fetch(`/api/clients/${clientId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clientId]);

  if (loading) return (
    <Modal onClose={closeModal} title="Detalle del cliente" width={720}>
      <div className="py-16 text-center text-ink-400 text-sm">Cargando...</div>
    </Modal>
  );
  if (!data) return (
    <Modal onClose={closeModal} title="Cliente no encontrado" width={720}>
      <div className="py-16 text-center text-ink-400 text-sm">No se pudo cargar la información.</div>
    </Modal>
  );

  const mailTypeLabel = { SOLICITUD: 'Solicitud', PRESUPUESTO: 'Presupuesto', NOTA_PEDIDO: 'Nota de Pedido', OC: 'OC' };
  const mailTypeColor = { SOLICITUD: 'bg-blue-50 text-blue-700', PRESUPUESTO: 'bg-purple-50 text-purple-700', NOTA_PEDIDO: 'bg-orange-50 text-orange-700' };

  const actionIcon = {
    CREATED:      { icon: 'plus-circle', color: 'text-ok' },
    STAGE_CHANGE: { icon: 'arrow-right', color: 'text-brand' },
    NOTE:         { icon: 'message-square', color: 'text-ink-400' },
    ASSIGNED:     { icon: 'user', color: 'text-purple-500' },
    EMAIL_SENT:   { icon: 'send', color: 'text-blue-500' },
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('es-AR', { day:'2-digit', month:'short', year:'numeric' });
  const fmtTime = (d) => new Date(d).toLocaleString('es-AR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });

  // Calcular totales por moneda
  const totalUSD = data.quotes.filter(q => (q.currency||'USD') !== 'ARS').reduce((s, q) => s + (q.amount || 0), 0);
  const totalARS = data.quotes.filter(q => q.currency === 'ARS').reduce((s, q) => s + (q.amount || 0), 0);
  const wonCount = data.quotes.filter(q => q.stage === 'aceptada').length;
  const activeCount = data.quotes.filter(q => !['aceptada','rechazada'].includes(q.stage)).length;

  return (
    <Modal onClose={closeModal} title={data.name} subtitle={data.cuit || data.code} width={780}
      footer={
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={() => { closeModal(); openModal('editClient', { clientId }); }}>
            <Icon name="pencil" size={13}/>Editar cliente
          </button>
          <button className="btn-ghost" onClick={closeModal}>Cerrar</button>
        </div>
      }
    >
      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Cotizaciones', value: data.quotes.length, color: 'text-ink-800' },
          { label: 'Activas', value: activeCount, color: 'text-brand' },
          { label: 'Ganadas', value: wonCount, color: 'text-ok' },
          { label: 'Monto total', value: totalUSD > 0 || totalARS > 0
            ? [totalUSD > 0 && fmtMoney(Math.round(totalUSD), 'USD'), totalARS > 0 && fmtMoney(Math.round(totalARS), 'ARS')].filter(Boolean).join(' + ')
            : '—', color: 'text-ink-800' },
        ].map(k => (
          <div key={k.label} className="bg-surface rounded-lg p-3 text-center">
            <div className="text-[10px] font-medium text-ink-400 uppercase tracking-wider">{k.label}</div>
            <div className={cx('text-lg font-bold mt-0.5', k.color)}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Info del cliente */}
      <div className="bg-surface rounded-lg p-4 mb-5 grid grid-cols-2 gap-x-6 gap-y-2 text-[12.5px]">
        {data.email && <div><span className="text-ink-400">Email:</span> <span className="text-ink-800">{data.email}</span></div>}
        {data.phone && <div><span className="text-ink-400">Tel:</span> <span className="text-ink-800">{data.phone}</span></div>}
        {data.address && <div><span className="text-ink-400">Dir:</span> <span className="text-ink-800">{data.address}</span></div>}
        {data.city && <div><span className="text-ink-400">Localidad:</span> <span className="text-ink-800">{data.city}{data.province ? `, ${data.province}` : ''}</span></div>}
        {data.zone && <div><span className="text-ink-400">Zona:</span> <span className="text-ink-800">{data.zone}</span></div>}
        {data.defaultSeller && <div><span className="text-ink-400">Vendedor:</span> <span className="text-ink-800">{data.defaultSeller.name}</span></div>}
      </div>

      {/* Cotizaciones */}
      {data.quotes.length > 0 && (
        <div className="mb-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink-400 mb-2">Cotizaciones</div>
          <div className="border border-line rounded-lg overflow-hidden divide-y divide-line">
            {data.quotes.map(q => (
              <button key={q.id} onClick={() => { closeModal(); openModal('quoteDetail', { code: q.code }); }}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-surface text-left transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-mono font-semibold text-ink-800">{q.code}</span>
                    {q.mailType && <span className={cx('px-1.5 py-0.5 rounded text-[10px] font-semibold', mailTypeColor[q.mailType] || 'bg-surface text-ink-500')}>{mailTypeLabel[q.mailType] || q.mailType}</span>}
                    {q.flexxusCode && <span className="text-[10px] text-ink-400 font-mono">{q.flexxusCode}</span>}
                  </div>
                  <div className="text-[11px] text-ink-400 mt-0.5">
                    {fmtDate(q.createdAt)}{q.seller ? ` · ${q.seller.name}` : ''}{q.amount ? ` · ${fmtMoney(Math.round(q.amount), q.currency)}` : ''}
                  </div>
                </div>
                <span className={cx('px-2 py-0.5 rounded text-[10px] font-semibold',
                  q.stage === 'aceptada' ? 'bg-emerald-50 text-emerald-700' :
                  q.stage === 'rechazada' ? 'bg-red-50 text-red-700' :
                  'bg-surface text-ink-500'
                )}>{q.stage}</span>
              </button>
            ))}
          </div>
        </div>
      )}


      {/* Timeline de actividades */}
      {data.activities?.length > 0 && (
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink-400 mb-2">Actividad reciente</div>
          <div className="space-y-0">
            {data.activities.map((a, i) => {
              const ai = actionIcon[a.action] || { icon: 'activity', color: 'text-ink-300' };
              const ref = a.quote?.code || a.order?.code || '';
              return (
                <div key={a.id} className="flex gap-3 py-2.5 group">
                  <div className="flex flex-col items-center">
                    <div className={cx('w-6 h-6 rounded-full flex items-center justify-center bg-surface shrink-0', ai.color)}>
                      <Icon name={ai.icon} size={12}/>
                    </div>
                    {i < data.activities.length - 1 && <div className="w-px flex-1 bg-line mt-1"/>}
                  </div>
                  <div className="flex-1 min-w-0 pb-1">
                    <div className="text-[12.5px] text-ink-700 leading-snug">
                      {ref && <span className="font-mono font-medium text-brand mr-1">{ref}</span>}
                      {(a.detail || '').replace(/\[.*?\]\s*/, '').substring(0, 200)}
                    </div>
                    <div className="text-[10.5px] text-ink-400 mt-0.5">
                      {fmtTime(a.createdAt)}{a.user?.name ? ` · ${a.user.name}` : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}

// --- Export PDF Modal ---
function ExportModal({ exportType }) {
  const { closeModal, pushToast } = useApp();
  const [mode, setMode] = useS('download'); // download | email
  const [to, setTo] = useS('');
  const [cc, setCc] = useS('');
  const [subject, setSubject] = useS('');
  const [body, setBody] = useS('');
  const [sending, setSending] = useS(false);
  const [sellerId, setSellerId] = useS('');
  const [from, setFrom] = useS('');
  const [toDate, setToDate] = useS('');

  const TYPES = {
    cotizaciones: { label: 'Cotizaciones', icon: 'clipboard-list' },
    rechazos:     { label: 'Rechazos',     icon: 'x-circle' },
    ordenes:      { label: 'Notas de Pedido', icon: 'truck' },
  };
  const typeInfo = TYPES[exportType] || TYPES.cotizaciones;

  const handleDownload = () => {
    const params = {};
    if (sellerId) params.sellerId = sellerId;
    if (from)     params.from = from;
    if (toDate)   params.to = toDate;
    const url = CrmApi.exportPdfUrl(exportType, params);
    // Abrir con token en header via fetch + blob
    const token = CrmAuth.getToken();
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error('Error al generar PDF');
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${exportType}_${new Date().toISOString().slice(0,10)}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
        pushToast('PDF descargado correctamente');
        closeModal();
      })
      .catch(err => pushToast(err.message || 'Error al descargar', 'bad'));
  };

  const handleSend = async () => {
    if (!to.trim()) return pushToast('Ingresá un destinatario', 'bad');
    setSending(true);
    try {
      const filters = {};
      if (sellerId) filters.sellerId = sellerId;
      if (from)     filters.from = from;
      if (toDate)   filters.to = toDate;
      await CrmApi.sendExportMail({ type: exportType, to: to.trim(), cc: cc.trim() || null, subject: subject.trim() || null, body: body.trim() || null, filters });
      pushToast('Reporte enviado por mail correctamente');
      closeModal();
    } catch (err) {
      pushToast(err.message || 'Error al enviar', 'bad');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal onClose={closeModal} title={`Exportar ${typeInfo.label}`}
      subtitle="Generar reporte PDF"
      width={520}
      footer={
        <>
          <button className="btn-ghost" onClick={closeModal}>Cancelar</button>
          {mode === 'download'
            ? <button className="btn-primary" onClick={handleDownload}><Icon name="download" size={13}/>Descargar PDF</button>
            : <button className="btn-primary" disabled={!to.trim() || sending} onClick={handleSend}
                style={(!to.trim() || sending) ? {opacity:.45, cursor:'not-allowed'} : {}}>
                <Icon name={sending ? 'loader' : 'send'} size={13}/>{sending ? 'Enviando…' : 'Enviar por mail'}
              </button>
          }
        </>
      }>
      {/* Mode toggle */}
      <div className="flex gap-1 mb-4 bg-surface rounded-lg p-1 border border-line">
        {[{id:'download', icon:'download', label:'Descargar'}, {id:'email', icon:'mail', label:'Enviar por mail'}].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
            className={cx('flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-[12px] font-medium transition-all',
              mode === m.id ? 'bg-white shadow-sm text-ink-900 border border-line' : 'text-ink-500 hover:text-ink-700')}>
            <Icon name={m.icon} size={13}/>{m.label}
          </button>
        ))}
      </div>

      {/* Filtros opcionales */}
      <div className="bg-surface border border-line rounded-lg p-3 mb-4 space-y-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Filtros del reporte (opcional)</div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] text-ink-500 block mb-1">Desde</label>
            <input type="date" className="inp w-full text-[12px]" value={from} onChange={e=>setFrom(e.target.value)}/>
          </div>
          <div>
            <label className="text-[11px] text-ink-500 block mb-1">Hasta</label>
            <input type="date" className="inp w-full text-[12px]" value={toDate} onChange={e=>setToDate(e.target.value)}/>
          </div>
          <div>
            <label className="text-[11px] text-ink-500 block mb-1">Vendedor</label>
            <select className="inp w-full text-[12px]" value={sellerId} onChange={e=>setSellerId(e.target.value)}>
              <option value="">Todos</option>
              {(window.__exportUsers || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Email fields */}
      {mode === 'email' && (
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-medium text-ink-500 block mb-1">Destinatario *</label>
            <input className="inp w-full" placeholder="email@ejemplo.com" value={to} onChange={e=>setTo(e.target.value)}/>
          </div>
          <div>
            <label className="text-[11px] font-medium text-ink-500 block mb-1">CC (opcional)</label>
            <input className="inp w-full" placeholder="otro@ejemplo.com" value={cc} onChange={e=>setCc(e.target.value)}/>
          </div>
          <div>
            <label className="text-[11px] font-medium text-ink-500 block mb-1">Asunto (opcional)</label>
            <input className="inp w-full" placeholder={`Reporte ${typeInfo.label} — Myselec CRM`} value={subject} onChange={e=>setSubject(e.target.value)}/>
          </div>
          <div>
            <label className="text-[11px] font-medium text-ink-500 block mb-1">Mensaje (opcional)</label>
            <textarea className="inp w-full resize-none" rows="3"
              placeholder="Se incluye un mensaje por defecto si lo dejás vacío"
              value={body} onChange={e=>setBody(e.target.value)}/>
          </div>
        </div>
      )}
    </Modal>
  );
}

const MODAL_REGISTRY = {
  newQuote: NewQuoteModal,
  newClient: NewClientModal,
  editClient: EditClientModal,
  clientDetail: ClientDetailModal,
  inviteUser: InviteUserModal,
  permissions: PermissionsModal,
  search: SearchPaletteModal,
  quoteDetail: QuoteDetailWrapper,
  exportPdf: ExportModal,
};

// ---------- Notifications Popover ----------
function NotificationsPopover({ onClose, setScreen }) {
  const { notifications, markNotificationRead, markAllNotificationsRead, openModal,
          inboxAlerts, setInboxAlerts, snoozeAlert, markInboxSeen, ackAssigned, ackMention, setQuoteFilters } = useApp();
  const [tab, setTab] = useS(inboxAlerts.length > 0 ? 'inbox' : 'activity');
  const [dismissOpen, setDismissOpen] = useS(null); // alert.id with open dismiss dropdown
  const [abiertas, setAbiertas] = useS(() => new Set()); // alertas con la lista desplegada ("+N más")

  // Llama mark-seen al abrir la pestaña inbox
  const switchToInbox = useCallback(() => {
    setTab('inbox');
    markInboxSeen();
  }, [markInboxSeen]);

  // mark-seen también al montar si ya estamos en inbox
  useEff(() => {
    if (tab === 'inbox') markInboxSeen();
  }, []);

  const toneClass  = { ok:'bg-emerald-500', bad:'bg-red-500', warn:'bg-orange-500', info:'bg-brand' };
  const sevColor   = { high:'text-red-500 bg-red-50 border-red-100',
                       medium:'text-orange-500 bg-orange-50 border-orange-100',
                       low:'text-brand bg-brandSoft border-brandSoft' };
  const sevDot     = { high:'bg-red-400', medium:'bg-orange-400', low:'bg-brand' };

  // State para el modal de recordatorio
  const [reminderItem, setReminderItem] = useS(null); // item de la alerta para enviar recordatorio

  // Mapa de iconos por tipo de alerta
  const alertIcon = {
    UNASSIGNED_QUOTES:     '👤',
    UNLINKED_PRESUPUESTOS: '🔗',
    PENDING_USERS:         '✅',
    OVERDUE_STAGES:        '⏰',
    IDLE_QUOTES:           '💤',
    FOLLOW_UP_DUE:         '📅',
    FOLLOW_UP_UPCOMING:    '📆',
    UNLINKED_SOLICITUDES:  '📋',
    NO_RESPONSE:           '📨',
    ASSIGNED_QUOTES:       '🎯',
    DEADLINE_OVERDUE:      '🚩',
    MENTIONS:              '🏷️',
  };

  const handleAlertAction = (alert) => {
    onClose();
    const view = alert.action?.view;
    // Antes solo cambiaba de pantalla: si ya estabas en Cotizaciones parecía no
    // hacer nada (MYS-0022). Ahora el tablero queda filtrado en esas cotizaciones.
    const ids = alert.action?.filter?.ids;
    if (view === 'quotes' && ids?.length) {
      setQuoteFilters(s => ({ ...s, ids, idsLabel: alert.action.filter.label || alert.title }));
    }
    if (view === 'quotes' && setScreen) setScreen('quotes');
    else if (view === 'team' && setScreen) setScreen('team');
    else openModal('search'); // fallback
  };

  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose}/>
      <div className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-xl shadow-pop border border-line modal-enter z-40 overflow-hidden">
        {/* Header con tabs */}
        <div className="px-4 pt-3 pb-0 border-b border-line">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-ink-900">Notificaciones</div>
            {tab === 'activity' && (
              <button onClick={markAllNotificationsRead} className="text-[11px] text-brand hover:underline">
                Marcar todas como leídas
              </button>
            )}
          </div>
          <div className="flex gap-1">
            <button onClick={switchToInbox}
              className={cx('px-3 py-1.5 text-[12px] font-medium rounded-t-md border-b-2 transition-colors',
                tab === 'inbox' ? 'border-brand text-brand' : 'border-transparent text-ink-400 hover:text-ink-700')}>
              Pendiente
              {inboxAlerts.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-bad text-white text-[10px] font-bold">
                  {inboxAlerts.length}
                </span>
              )}
            </button>
            <button onClick={() => setTab('activity')}
              className={cx('px-3 py-1.5 text-[12px] font-medium rounded-t-md border-b-2 transition-colors',
                tab === 'activity' ? 'border-brand text-brand' : 'border-transparent text-ink-400 hover:text-ink-700')}>
              Actividad
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-brand text-white text-[10px] font-bold">
                  {notifications.filter(n => !n.read).length}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="max-h-[440px] overflow-y-auto scroll-thin">
          {/* Tab: Pendiente (inbox alerts) */}
          {tab === 'inbox' && (
            <>
              {inboxAlerts.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                    <Icon name="check-circle" size={20} className="text-emerald-500"/>
                  </div>
                  <div className="text-ink-700 text-[13px] font-medium">Todo al día</div>
                  <div className="text-ink-400 text-[11px] mt-0.5">Sin pendientes por atender</div>
                </div>
              ) : (
                <div className="p-3 space-y-2">
                  {inboxAlerts.map(alert => (
                    <div key={alert.id}
                      className={cx('rounded-lg border p-3', sevColor[alert.severity] || sevColor.low)}>
                      <div className="flex items-start gap-2">
                        <span className="text-base leading-none mt-0.5">{alertIcon[alert.type] || '📌'}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <div className="text-[13px] font-semibold leading-snug">{alert.title}</div>
                            {alert.newCount > 0 && (
                              <span className="px-1.5 py-0.5 rounded-full bg-bad text-white text-[9px] font-bold shrink-0">
                                {alert.newCount} nuevo{alert.newCount > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {alert.description && (
                            <div className="text-[11px] mt-0.5 opacity-80 leading-snug">{alert.description}</div>
                          )}
                          {/* Mini-lista: top 3 ítems de la alerta */}
                          {alert.items?.length > 0 && (
                            <div className="mt-1.5 space-y-0.5">
                              {alert.items.slice(0, abiertas.has(alert.id) ? alert.items.length : 3).map((item, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-[10.5px] opacity-70 leading-snug">
                                  {item.code && alert.type !== 'MENTIONS' ? (
                                    <button title="Abrir"
                                      onClick={(e) => { e.stopPropagation(); onClose(); openModal('quoteDetail', { code: item.code }); }}
                                      className="font-mono font-medium shrink-0 underline decoration-dotted underline-offset-2 hover:decoration-solid">
                                      {item.code}
                                    </button>
                                  ) : <span className="font-mono font-medium">{item.code}</span>}
                                  {item.clientName && <span className="truncate">· {item.clientName}</span>}
                                  {item.daysSent !== undefined && <span className="shrink-0 text-[10px]">· {item.daysSent}d</span>}
                                  {item.daysOld !== undefined && <span className="shrink-0 text-[10px]">· {item.daysOld}d</span>}
                                  {item.followUpDate && (
                                    <span className="shrink-0 text-[10px]">· {new Date(item.followUpDate).toLocaleDateString('es-AR', { day:'2-digit', month:'short' })}</span>
                                  )}
                                  {item.stage && !item.clientName && <span className="opacity-60">· {item.stage}</span>}
                                  {item.assignedBy && alert.type === 'ASSIGNED_QUOTES' && (
                                    <>
                                      <span className="shrink-0 text-[10px] opacity-70">por {item.assignedBy}</span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); ackAssigned(item.id); }}
                                        className="shrink-0 ml-auto px-1.5 py-0.5 rounded bg-white/80 hover:bg-white text-[9px] font-semibold border border-current/20">
                                        Listo ✓
                                      </button>
                                    </>
                                  )}
                                  {item.canRemind && alert.type === 'NO_RESPONSE' && (
                                    <button onClick={(e) => { e.stopPropagation(); setReminderItem(item); }}
                                      className="shrink-0 ml-auto px-1.5 py-0.5 rounded bg-white/80 hover:bg-white text-[9px] font-semibold border border-current/20">
                                      Recordar
                                    </button>
                                  )}
                                  {alert.type === 'MENTIONS' && (
                                    <>
                                      {item.byName && <span className="shrink-0 text-[10px] opacity-70">por {item.byName}</span>}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          ackMention(item.noteId);
                                          onClose();
                                          openModal('quoteDetail', { code: item.code });
                                        }}
                                        className="shrink-0 ml-auto px-1.5 py-0.5 rounded bg-white/80 hover:bg-white text-[9px] font-semibold border border-current/20">
                                        Ver
                                      </button>
                                    </>
                                  )}
                                </div>
                              ))}
                              {/* "+N más" despliega el resto de la lista ahí mismo. El total
                                  sale de count: el servidor manda como mucho MAX_ITEMS ítems. */}
                              {alert.items.length > 3 && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setAbiertas(s => { const n = new Set(s); n.has(alert.id) ? n.delete(alert.id) : n.add(alert.id); return n; }); }}
                                  className="text-[10px] opacity-60 hover:opacity-100 underline decoration-dotted underline-offset-2">
                                  {abiertas.has(alert.id) ? 'Ver menos' : `+${(alert.count || alert.items.length) - 3} más`}
                                </button>
                              )}
                              {abiertas.has(alert.id) && (alert.count || 0) > alert.items.length && (
                                <div className="text-[10px] opacity-50">y {alert.count - alert.items.length} más en el tablero</div>
                              )}
                            </div>
                          )}
                        </div>
                        <span className={cx('w-2 h-2 rounded-full mt-1 shrink-0', sevDot[alert.severity])}/>
                      </div>
                      <div className="flex gap-2 mt-2.5">
                        {alert.action && (
                          <button onClick={() => handleAlertAction(alert)}
                            className="px-2.5 py-1 rounded-md bg-white/70 hover:bg-white text-[11px] font-medium border border-current/20 transition-colors">
                            {alert.action.label}
                          </button>
                        )}
                        {alert.dismissable && (
                          <div className="relative">
                            <button
                              onClick={() => setDismissOpen(dismissOpen === alert.id ? null : alert.id)}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium opacity-60 hover:opacity-100 transition-opacity">
                              Posponer ▾
                            </button>
                            {dismissOpen === alert.id && (
                              <div className="absolute left-0 top-full mt-1 bg-white border border-line rounded-lg shadow-pop z-50 py-1 min-w-[110px]">
                                {[3, 7, 30].map(d => (
                                  <button key={d}
                                    onClick={() => { snoozeAlert(alert.id, alert.dismissKey, d); setDismissOpen(null); }}
                                    className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-surface transition-colors">
                                    {d} días
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Tab: Actividad (feed histórico) */}
          {tab === 'activity' && (
            <>
              {notifications.length === 0 && (
                <div className="py-12 text-center">
                  <div className="w-10 h-10 rounded-full bg-surface flex items-center justify-center mx-auto mb-3">
                    <Icon name="bell-off" size={20} className="text-ink-300"/>
                  </div>
                  <div className="text-ink-700 text-[13px] font-medium">Sin actividad</div>
                  <div className="text-ink-400 text-[11px] mt-0.5">Las notificaciones aparecerán aquí</div>
                </div>
              )}
              {notifications.map(n => (
                <button key={n.id}
                  onClick={()=>{
                    markNotificationRead(n.id);
                    onClose();
                    if (n.ref?.kind==='quote') openModal('quoteDetail', { code: n.ref.code });
                  }}
                  className={cx('w-full text-left px-4 py-3 border-b border-line/60 flex gap-3 items-start transition-all duration-150',
                    !n.read ? 'bg-brandSoft/20 hover:bg-brandSoft/35' : 'hover:bg-surface')}>
                  <span className={cx('w-2 h-2 rounded-full mt-1.5 shrink-0', toneClass[n.kind])}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] leading-snug text-ink-900">{n.text}</div>
                    <div className="text-[11px] text-ink-500 mt-0.5 mono">{fmtDateTime(n.at)}</div>
                  </div>
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-brand mt-2"/>}
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Modal de recordatorio — preview y envío */}
      {reminderItem && (() => {
        const ri = reminderItem;
        const defaultSubject = `Seguimiento presupuesto ${ri.flexxusCode || ri.code} — MySelec`;
        const defaultBody = `Hola ${ri.clientName || ''},\n\nTe escribimos para hacer seguimiento del presupuesto ${ri.flexxusCode || ri.code} que te enviamos hace ${ri.daysSent} días.\n\n¿Pudiste revisarlo? Quedamos a disposición para cualquier consulta.\n\nSaludos cordiales,\nEquipo MySelec`;

        return <ReminderModal
          item={ri}
          defaultSubject={defaultSubject}
          defaultBody={defaultBody}
          onClose={() => setReminderItem(null)}
          onSent={() => {
            // Quitar el item de la alerta local (se actualiza en el próximo polling)
            setInboxAlerts(prev => prev.map(a => {
              if (a.type !== 'NO_RESPONSE') return a;
              const newItems = (a.items || []).filter(it => it.id !== ri.id);
              if (newItems.length === 0) return null;
              return { ...a, items: newItems, count: newItems.length, title: `${newItems.length} presupuesto${newItems.length > 1 ? 's' : ''} sin respuesta` };
            }).filter(Boolean));
            setReminderItem(null);
          }}
        />;
      })()}
    </>
  );
}

// ---------- ReminderModal — preview y envío de recordatorio ----------
function ReminderModal({ item, defaultSubject, defaultBody, onClose, onSent }) {
  const { pushToast } = useApp();
  const [subject, setSubject] = useS(defaultSubject);
  const [body, setBody]       = useS(defaultBody);
  const [sending, setSending] = useS(false);

  const handleSend = async () => {
    setSending(true);
    try {
      await CrmApi.sendReminder(item.id, { subject, body });
      pushToast('Recordatorio enviado a ' + item.clientEmail, 'ok');
      onSent();
    } catch (err) {
      pushToast(err.message || 'Error al enviar', 'bad');
    } finally {
      setSending(false);
    }
  };

  const buildGmailUrl = () => {
    const params = new URLSearchParams();
    if (item.clientEmail) params.set('to', item.clientEmail);
    params.set('su', subject || '');
    params.set('body', body || '');
    params.set('view', 'cm'); params.set('fs', '1');
    return 'https://mail.google.com/mail/?' + params.toString();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 modal-overlay" onClick={onClose}/>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-pop w-full max-w-lg border border-line modal-enter" onClick={e => e.stopPropagation()}>
          <div className="px-5 py-4 border-b border-line flex items-center justify-between">
            <div>
              <div className="font-semibold text-ink-900 text-[14px]">Enviar recordatorio</div>
              <div className="text-[11.5px] text-ink-400 mt-0.5">
                {item.code} · {item.clientName} · {item.daysSent}d sin respuesta
              </div>
            </div>
            <button onClick={onClose} className="btn-ghost p-1"><Icon name="x" size={16}/></button>
          </div>
          <div className="p-5 space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-ink-500 mb-1">Para</label>
              <input className="inp w-full bg-surface text-ink-500 cursor-not-allowed text-[13px]" value={item.clientEmail} readOnly/>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-ink-500 mb-1">Asunto</label>
              <input className="inp w-full text-[13px]" value={subject} onChange={e => setSubject(e.target.value)}/>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-ink-500 mb-1">Mensaje</label>
              <textarea className="inp w-full text-[13px] min-h-[160px] leading-relaxed" value={body} onChange={e => setBody(e.target.value)}/>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-[11px] text-blue-700 space-y-1">
              <div>Se envía desde <strong>iamyselec@gmail.com</strong>. Para enviar desde tu Gmail personal usá "Abrir en Gmail".</div>
              <div>Al enviar, se registrará como actividad y se reprogramará el seguimiento automáticamente.</div>
            </div>
          </div>
          <div className="px-5 py-4 border-t border-line flex items-center justify-between">
            <button onClick={onClose} className="btn-ghost" disabled={sending}>Cancelar</button>
            <div className="flex items-center gap-2">
              <button onClick={handleSend} className="btn-primary" disabled={sending || !subject || !body}>
                <Icon name="send" size={13}/>{sending ? 'Enviando...' : 'Enviar recordatorio'}
              </button>
              <button className="btn-ghost border border-line" onClick={() => window.open(buildGmailUrl(), '_blank')}>
                <Icon name="external-link" size={13}/>Abrir en Gmail
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------- More Filters Popover (Quotes) ----------
function MoreFiltersPopover({ onClose, which='quote' }) {
  const { quoteFilters, setQuoteFilters } = useApp();
  const f = quoteFilters;
  const set = setQuoteFilters;
  const reset = () => set(s => ({ ...s, min:'', max:'', hasNotes:false, priority:'', porRechazarse:false, mencionado:false }));
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose}/>
      <div className="absolute right-0 top-full mt-2 w-[340px] bg-white rounded-xl shadow-pop border border-line modal-enter z-40 p-4">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-3">Filtros avanzados</div>
        <div className="space-y-3">
          <FormGroup label="Monto mínimo (ARS)">
            <input type="number" className="inp w-full" value={f.min||''} onChange={e=>set(s=>({...s, min:e.target.value}))} placeholder="0"/>
          </FormGroup>
          <FormGroup label="Monto máximo (ARS)">
            <input type="number" className="inp w-full" value={f.max||''} onChange={e=>set(s=>({...s, max:e.target.value}))} placeholder="Sin límite"/>
          </FormGroup>
          {which === 'quote' && (
            <FormGroup label="Seguimiento">
              <div className="flex flex-wrap gap-1.5">
                <button onClick={()=>set(s=>({...s, priority:''}))}
                  className={cx('px-2.5 py-1 rounded-lg text-[12px] border transition-colors',
                    !f.priority ? 'border-brand bg-brandSoft/40 text-navy-900 font-medium' : 'border-line text-ink-600 hover:bg-surface')}>
                  Todas
                </button>
                {PRIORIDADES.map(p => (
                  <button key={p.id} title={p.desc}
                    onClick={()=>set(s=>({...s, priority: f.priority===p.id ? '' : p.id}))}
                    className={cx('px-2.5 py-1 rounded-lg text-[12px] border flex items-center gap-1.5 transition-colors',
                      f.priority===p.id ? 'font-medium text-navy-900' : 'border-line text-ink-600 hover:bg-surface')}
                    style={f.priority===p.id ? { background:p.wash, borderColor:p.line } : {}}>
                    <span className="w-2 h-2 rounded-full" style={{ background:p.dot }}/>{p.label}
                  </button>
                ))}
                <button onClick={()=>set(s=>({...s, priority: f.priority==='none' ? '' : 'none'}))}
                  className={cx('px-2.5 py-1 rounded-lg text-[12px] border transition-colors',
                    f.priority==='none' ? 'border-brand bg-brandSoft/40 text-navy-900 font-medium' : 'border-line text-ink-600 hover:bg-surface')}>
                  Sin marcar
                </button>
              </div>
            </FormGroup>
          )}
          {which === 'quote' && (
            <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer select-none">
              <input type="checkbox" checked={!!f.hasNotes} onChange={e=>set(s=>({...s, hasNotes:e.target.checked}))} className="w-4 h-4 rounded border-line accent-brand"/>
              Solo con notas
            </label>
          )}
          {which === 'quote' && (
            <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer select-none"
              title="Paquetes con alguna nota donde te mencionaron con @, la hayas visto o no, de cualquier fecha">
              <input type="checkbox" checked={!!f.mencionado} onChange={e=>set(s=>({...s, mencionado:e.target.checked}))} className="w-4 h-4 rounded border-line accent-brand"/>
              Donde me mencionaron
            </label>
          )}
          {which === 'quote' && (
            <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer select-none"
              title="Presupuestos enviados sin semáforo que la regla de los 30 días rechaza solos en los próximos 7 días">
              <input type="checkbox" checked={!!f.porRechazarse} onChange={e=>set(s=>({...s, porRechazarse:e.target.checked}))} className="w-4 h-4 rounded border-line accent-brand"/>
              Por rechazarse solos (próximos 7 días)
            </label>
          )}
        </div>
        <div className="flex gap-2 pt-3 mt-3 border-t border-line">
          <button className="btn-ghost flex-1 justify-center" onClick={reset}>Restablecer</button>
          <button className="btn-primary flex-1 justify-center" onClick={onClose}>Aplicar</button>
        </div>
      </div>
    </>
  );
}

// ---------- Filter helpers ----------
function periodStartDate(period) {
  const now = new Date();
  if (period === '7d')      { const d = new Date(now); d.setDate(d.getDate() - 7);  return d; }
  if (period === '30d')     { const d = new Date(now); d.setDate(d.getDate() - 30); return d; }
  if (period === 'month')   { return new Date(now.getFullYear(), now.getMonth(), 1); }
  if (period === 'quarter') { return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1); }
  return null; // 'all'
}

function countActiveFilters(f) {
  let n = 0;
  if (f.seller) n++;
  if (f.client) n++;
  if (f.period && f.period !== '30d') n++;
  if (f.min) n++;
  if (f.max) n++;
  if (f.hasNotes) n++;
  if (f.porRechazarse) n++;
  if (f.mencionado) n++;
  if (f.priority) n++;
  if (f.delivery) n++;
  if (f.transport) n++;
  if (f.ids) n++;
  return n;
}

function applyQuoteFilters(list, filters, clientsArr) {
  // Filtro que pone una alerta de la campanita ("Ver solicitudes"): solo esas
  // cotizaciones, sin mirar el período — las más viejas son justo las que avisa.
  const soloIds = filters.ids ? new Set(filters.ids) : null;
  // Tampoco al buscar "donde me mencionaron": la mención puede ser de hace meses y el período por defecto es 30 días.
  const periodStart = filters.period && !soloIds && !filters.mencionado ? periodStartDate(filters.period) : null;
  // La tarjeta de un paquete muestra la marca del documento principal. El filtro tiene que mirar esa
  // misma, no la de cada documento: si no, una solicitud con una marca vieja pasaba el filtro por su
  // cuenta, su presupuesto no, y la solicitud aparecía SOLA, sin su paquete (MYS-0027).
  const marcaDe = new Map(list.map(x => [x.id, x.priority || null]));
  const marcaDeLaTarjeta = (q) => (q.paquete && q.paquete.principal !== q.id && marcaDe.has(q.paquete.principal))
    ? marcaDe.get(q.paquete.principal) : (q.priority || null);
  // "Donde me mencionaron" (MYS-0028): el paquete entra entero si alguna nota de cualquiera de sus
  // documentos menciona al usuario; si no, quedaría la tarjeta suelta del documento con la nota.
  const yo = filters.mencionado ? CrmAuth.getUser()?.id : null;
  const conMencion = new Set();
  if (yo) for (const x of list) if ((x.mencionados || []).includes(yo)) conMencion.add(x.paquete ? x.paquete.principal : x.id);
  return list.filter(q => {
    if (soloIds && !soloIds.has(q.id)) return false;
    // Una anulada fue reemplazada por una revisión: mostrarla sería tener dos
    // presupuestos vivos del mismo negocio. Se abre desde la ficha de la nueva.
    if (q.anulada) return false;
    if (filters.seller === 'UNASSIGNED') { if (q.seller) return false; }
    else if (filters.seller && q.seller !== filters.seller) return false;
    if (periodStart && new Date(q.ingreso) < periodStart) return false;
    const cli = clientsArr.find(c => c.code === q.client);
    if (filters.client && !coincideBusqueda(filters.client, cli?.name || q.clientName || q.emailSubject, cli?.cuit, q.code, q.flexxus)) return false;
    if (filters.min && (q.monto||0) < parseFloat(filters.min)) return false;
    if (filters.max && (q.monto||0) > parseFloat(filters.max)) return false;
    if (filters.hasNotes && !(q.notas > 0)) return false;
    if (yo && !conMencion.has(q.paquete ? q.paquete.principal : q.id)) return false;
    // Los que la regla de rechazo automático va a rechazar en 7 días o menos (sin semáforo; ver seguimientoDe)
    if (filters.porRechazarse) { const s = seguimientoDe(q); if (!(s && s.estado === 'cuenta' && s.faltan <= 7)) return false; }
    // 'none' = las que el vendedor todavía no marcó
    if (filters.priority === 'none') { if (marcaDeLaTarjeta(q)) return false; }
    else if (filters.priority && marcaDeLaTarjeta(q) !== filters.priority) return false;
    return true;
  });
}
// Orden de las tarjetas dentro de cada columna del tablero.
// 'recent' = como viene del backend (createdAt desc). Las cotizaciones sin monto
// (típicamente Solicitudes aún no presupuestadas) van al final, no como cero.
function sortQuotes(list, sort) {
  if (!sort || sort === 'recent') return list;
  const dir = sort === 'amount_asc' ? 1 : -1;
  const byRecent = (a, b) => new Date(b.ingreso) - new Date(a.ingreso);
  return [...list].sort((a, b) => {
    const av = a.monto, bv = b.monto;
    const aNull = av == null || av === '', bNull = bv == null || bv === '';
    if (aNull && bNull) return byRecent(a, b);
    if (aNull) return 1;
    if (bNull) return -1;
    if (av === bv) return byRecent(a, b);
    return (av - bv) * dir;
  });
}
// ---------- Popover button wrapper ----------
function PopoverButton({ icon, label, value, onClear, options, onChange, active }) {
  const [open, setOpen] = useS(false);
  return (
    <div className="relative">
      <button onClick={()=>setOpen(o=>!o)} className={cx(
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border',
        active ? 'bg-brandSoft text-navy-900 border-brand/40' : 'bg-white text-ink-700 border-line hover:bg-surface'
      )}>
        {icon && <Icon name={icon} size={13}/>}
        {label}
        <Icon name="chevron-down" size={11} className="opacity-60"/>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={()=>setOpen(false)}/>
          <div className="absolute left-0 top-full mt-2 w-[240px] bg-white rounded-xl shadow-pop border border-line modal-enter z-40 overflow-hidden">
            {active && onClear && (
              <button onClick={()=>{ onClear(); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-[12px] text-bad hover:bg-red-50 border-b border-line flex items-center gap-2">
                <Icon name="x" size={12}/> Limpiar filtro
              </button>
            )}
            {options.map(opt => (
              <button key={opt.value||'all'} onClick={()=>{ onChange(opt.value); setOpen(false); }}
                className={cx('w-full text-left px-3 py-2 text-[13px] hover:bg-surface flex items-center gap-2',
                  value === opt.value && 'bg-brandSoft/40 font-medium')}>
                {opt.icon && <Icon name={opt.icon} size={12} className="text-ink-500"/>}
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

Object.assign(window, {
  AppProvider, useApp, Modal,
  REJECT_REASONS,
  NotificationsPopover, MoreFiltersPopover, PopoverButton, BuscadorCliente,
  applyQuoteFilters, countActiveFilters, sortQuotes,
});
